import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import logo from "./../../assets/images/new-logo.svg";
import { useTranslation } from "../../contexts/LanguageContext";
import { fetchLanguages } from "../../redux/slices/languagesSlice";

const SignIn = ({ handleSignIn, loading, error }) => {
  const dispatch = useDispatch();
  const { t, setLanguage, activeLanguageKey } = useTranslation();
  const { languages: apiLanguages = [], loading: languagesLoading } = useSelector(
    (state) => state.languages || {}
  );

  // Fetch language list once so the dropdown can be populated before login.
  useEffect(() => {
    if (apiLanguages.length === 0) {
      dispatch(fetchLanguages({ currentPage: 1, limit: 100 }));
    }
  }, [dispatch, apiLanguages.length]);

  // Languages bundled with the app (en.json + ar.json). The LanguageContext
  // already knows how to apply the local ar.json when no CDN URL is provided,
  // so these always work even without an auth token (which the login page
  // doesn't have yet).
  const bundledLanguages = [
    { key: "en", name: "English", url: "" },
    { key: "ar", name: "Arabic", url: "" },
  ];

  const handleLanguageChange = (e) => {
    const key = e.target.value;
    // Prefer the API entry (it has a real CDN url) if available; otherwise
    // fall back to the bundled language so the local JSON is applied.
    const fromApi = apiLanguages.find((l) => l.key === key);
    const fromBundle = bundledLanguages.find((l) => l.key === key);
    const picked = fromApi || fromBundle;
    if (picked) setLanguage(picked);
  };

  // Merge bundled + API languages, de-duped by key. Bundled entries guarantee
  // the dropdown is populated on the login page even before the token exists.
  const options = (() => {
    const map = new Map();
    bundledLanguages.forEach((l) => map.set(l.key, l));
    apiLanguages.forEach((l) => map.set(l.key, l));
    return Array.from(map.values()).map((l) => ({ key: l.key, name: l.name }));
  })();

  return (
    <div className="flex items-center justify-center bg-gray-50 py-40">
      <div className="min-h-screen-minus-20 w-full mx-4 md:mx-0 md:max-w-5xl grid md:grid-cols-2 gap-0 rounded-3xl overflow-hidden shadow-lg">
        <div className="bg-themeGray flex items-center justify-center">
          <img src={logo} className="h-12 md:h-28 py-2 md:py-0" alt="Logo" />
        </div>
        <div className="bg-white p-6 px-16 flex items-center justify-center">
          <div className="w-full">
            {/* Language dropdown — picked before login, persists into the app */}
            <div className="flex justify-end mb-3">
              <select
                aria-label={t("signIn.language")}
                value={activeLanguageKey || "en"}
                onChange={handleLanguageChange}
                disabled={languagesLoading}
                className="text-sm border border-gray-300 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-themeOrange"
              >
                {options.map((l) => (
                  <option key={l.key} value={l.key}>{l.name}</option>
                ))}
              </select>
            </div>

            <h2 className="text-3xl font-semibold mb-5 text-center text-themeGray">
              {t("signIn.title")}
            </h2>
            <form onSubmit={handleSignIn} className="space-y-5">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-themeGrayLight">
                  {t("signIn.email")}
                </label>
                <input
                  type="email"
                  id="username"
                  name="username"
                  className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-themeOrange focus:border-themeOrange sm:text-sm"
                  required
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-themeGrayLight">
                  {t("signIn.password")}
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-themeOrange focus:border-themeOrange sm:text-sm"
                  required
                />
              </div>

              {loading && (
                <div className="flex justify-center">
                  <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full"></div>
                </div>
              )}

              {error && <p className="text-red-500 text-center">{error.message}</p>}

              <button
                type="submit"
                className="w-full py-2 px-4 bg-[#FF6F61] text-white font-semibold rounded-xl hover:bg-[#e5635a] focus:outline-none focus:ring-2 focus:ring-[#FF6F61] text-sm"
                disabled={loading}
              >
                {loading ? t("signIn.submitting") : t("signIn.submit")}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
